/**
 * Collapsible audit view over `GET /api/admin/sessions` (`session.admin`).
 *
 * The list is fetched LAZILY on first expand: it is audit data, not something
 * the user-administration screen needs on every visit, and the endpoint reads
 * the session table. Token hashes never leave the server, so everything here is
 * metadata — user, origin, sliding expiry and the hard cap.
 *
 * An empty list is a legitimate answer: with `AUTH_ENABLED=false` the backend
 * short-circuits and returns none, which the empty state says out loud.
 */

import { useCallback, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { Chip } from '../../components/Chip';
import DataTable from '../../components/DataTable';
import type { DataTableColumn } from '../../components/DataTable';
import { Icon } from '../../components/Icon';
import Spinner from '../../components/Spinner';
import { errorMessage, fetchAdminSessions } from '../../lib/api';
import { DASH, fmtDate, truncate } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type { SessionInfo } from '../../lib/types';

/** A session this close to its sliding expiry is flagged. */
const EXPIRY_WARN_MS = 5 * 60 * 1000;

function expiresSoon(session: SessionInfo): boolean {
  const due = new Date(session.expires_at).getTime();
  if (Number.isNaN(due)) return false;
  const remaining = due - Date.now();
  return remaining > 0 && remaining <= EXPIRY_WARN_MS;
}

export interface SessionsPanelProps {
  /** `has('session.admin')` — without it the panel explains itself instead. */
  allowed: boolean;
  /** E-mail of the signed-in account, marked in the table. */
  ownEmail: string | null;
}

export function SessionsPanel({ allowed, ownEmail }: SessionsPanelProps): ReactNode {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState<readonly SessionInfo[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAdminSessions();
      setSessions(response.sessions);
    } catch (err) {
      setError(errorMessage(err));
      setSessions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = useCallback((): void => {
    const next = !open;
    setOpen(next);
    // Fetch on the first expand only; the refresh button handles the rest.
    // Kept out of the state updater: that one may run twice (StrictMode).
    if (next && sessions === null && !loading) void load();
  }, [open, sessions, loading, load]);

  const columns: readonly DataTableColumn<SessionInfo>[] = [
    {
      key: 'user',
      header: 'Benutzer',
      sortValue: (row) => row.user_email,
      render: (row) => (
        <>
          <div className="kunde-name">{row.user_email !== '' ? row.user_email : DASH}</div>
          {ownEmail !== null && row.user_email.toLowerCase() === ownEmail.toLowerCase() ? (
            <div style={{ marginTop: 'var(--space-1)' }}>
              <Chip tone="info">Ihr Konto</Chip>
            </div>
          ) : null}
        </>
      ),
    },
    {
      key: 'ip',
      header: 'IP',
      sortValue: (row) => row.ip,
      render: (row) => <span className="mono">{row.ip ?? DASH}</span>,
    },
    {
      key: 'client',
      header: 'Client',
      render: (row) =>
        row.user_agent === null || row.user_agent === '' ? (
          DASH
        ) : (
          <span title={row.user_agent}>{truncate(row.user_agent, 48)}</span>
        ),
    },
    {
      key: 'created',
      header: 'Angemeldet',
      sortValue: (row) => row.created_at,
      render: (row) => <span>{fmtDate(row.created_at)}</span>,
    },
    {
      key: 'seen',
      header: 'Letzte Aktivität',
      sortValue: (row) => row.last_seen_at,
      render: (row) => <span>{fmtDate(row.last_seen_at)}</span>,
    },
    {
      key: 'expires',
      header: 'Läuft ab',
      headerTitle: 'Verlängert sich bei jeder Aktivität',
      sortValue: (row) => row.expires_at,
      render: (row) => (
        <div>
          <span>{fmtDate(row.expires_at)}</span>
          {expiresSoon(row) ? (
            <div style={{ marginTop: 'var(--space-1)' }}>
              <Chip tone="warn">läuft bald ab</Chip>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'absolute',
      header: 'Hartes Limit',
      headerTitle: 'Absolute Obergrenze, unabhängig von Aktivität',
      sortValue: (row) => row.absolute_expires_at,
      render: (row) => <span>{fmtDate(row.absolute_expires_at)}</span>,
    },
  ];

  return (
    <div className="card">
      <button
        type="button"
        className="adm-collapse"
        aria-expanded={open}
        onClick={toggle}
        disabled={!allowed}
        title={
          allowed
            ? undefined
            : missingPermissionTitle('session.admin')
        }
      >
        <Icon name="chevron-right" size={14} className="tw" />
        <span>Aktive Sitzungen</span>
        <span className="adm-collapse-note">
          {!allowed
            ? 'keine Berechtigung (session.admin)'
            : sessions === null
              ? 'wird beim Aufklappen geladen'
              : `${sessions.length} ${sessions.length === 1 ? 'Sitzung' : 'Sitzungen'}`}
        </span>
      </button>

      {open && allowed ? (
        <div className="adm-sessions">
          {error !== null ? (
            <div className="card-body">
              <Banner kind="error">{error}</Banner>
            </div>
          ) : null}

          {loading && sessions === null ? (
            <div className="empty">
              <Spinner /> Sitzungen werden geladen…
            </div>
          ) : sessions === null ? null : (
            <DataTable
              rows={sessions}
              columns={columns}
              rowKey={(row) => row.id}
              defaultSort={{ columnKey: 'seen', direction: 'desc' }}
              mobileBreakpoint={960}
              pageSize={10}
              resetPageOnRowsChange={false}
              caption="Aktive Sitzungen"
              emptyText="Keine aktiven Sitzungen. Bei abgeschalteter Anmeldung werden keine Sitzungen angelegt."
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

export default SessionsPanel;
